import { type Common } from './common.js';
import {
  type CellPipelineWithSteps,
  type CreateValues,
  type Feed,
  type FeedFunctionList,
  type FeedHistory,
  type FieldMapping,
  type FindAllOptions,
  type FindAllUploadsOptions,
  type ImportValidation,
  type PipelineContentWithSteps,
  type SyncJob,
  type UpdateValues,
  type Upload,
  type UploadLog,
  type UploadsWithStatisticsResponse,
} from './models/feed.js';
import { type PaginatedQueryOptions } from './models/pagination/PaginatedQueryOptions.type.js';
import { type PaginationResponse } from './models/pagination/PaginationResponse.type.js';

export class API {
  common: Common;
  baseEndpoint = '/feeds';

  constructor(common: Common) {
    this.common = common;
  }

  async findAll(query: FindAllOptions = {}): Promise<Feed[]> {
    const url = this.common.constructPath(this.baseEndpoint, query);
    return await this.common.getJSON(url);
  }

  async findAllPaginated(
    options: PaginatedQueryOptions<FindAllOptions>
  ): Promise<PaginationResponse<Feed>> {
    const url = this.common.constructPath(this.baseEndpoint, options);
    return await this.common.getJSON(url);
  }

  async findOneById(id: string): Promise<Feed> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.getJSON(url);
  }

  async expectedSourceFields(id: string): Promise<string[]> {
    const url = this.common.constructPath(`/feeds/${id}/expected-source-fields`);
    const { fields }: { fields: string[] } = await this.common.getJSON(url);
    return fields;
  }

  async create(toAdd: CreateValues): Promise<Feed> {
    const url = this.common.constructPath(this.baseEndpoint);
    return await this.common.postJSON(url, toAdd);
  }

  async update(id: string, toUpdate: UpdateValues): Promise<Feed> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.patchJSON(url, toUpdate);
  }

  async remove(id: string): Promise<boolean> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    const { success } = await this.common.deleteJSON(url);
    return success;
  }

  async sync(id: string): Promise<{ success: boolean; jobs: number; practices: number }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/sync`);
    return await this.common.postJSON(url, {});
  }

  async findHistory(feedId: string): Promise<FeedHistory[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${feedId}/history`);
    return await this.common.getJSON(url);
  }

  async findAllUploads(
    feedId: string,
    options?: FindAllUploadsOptions
  ): Promise<UploadsWithStatisticsResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${feedId}/uploads`, options);
    return await this.common.getJSON(url);
  }

  async findUploadByFeedIdAndUploadId(feedId: string, uploadId: string): Promise<Upload> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${feedId}/uploads/${uploadId}`);
    return await this.common.getJSON(url);
  }

  async getUploadLogs(feedId: string, uploadId: string): Promise<UploadLog[]> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${feedId}/uploads/${uploadId}/logs`
    );
    const { logs } = await this.common.getJSON(url);
    return logs;
  }

  getUploadDownloadUrl(args: { feedId: string; uploadId: string }) {
    return this.common.constructPath(
      `${this.baseEndpoint}/${args.feedId}/uploads/${args.uploadId}/file`
    );
  }

  async findDataBindings(query: { job: SyncJob }): Promise<string[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/data-bindings`, query);
    return await this.common.getJSON(url);
  }

  async findAllMappings(feedId: string): Promise<FieldMapping[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${feedId}/field-mappings`);
    const results: FieldMapping[] = await this.common.getJSON(url);
    return results.map((field) => ({
      field: field.field,
      dataBinding: field.dataBinding,
      transform: field.transform,
    }));
  }

  async upsertMappings(feedId: string, toUpsert: FieldMapping[]): Promise<FieldMapping[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${feedId}/field-mappings`);
    const results: FieldMapping[] = await this.common.putJSON(url, toUpsert);
    return results.map((field) => ({
      field: field.field,
      dataBinding: field.dataBinding,
      transform: field.transform,
    }));
  }

  async findAllPipelines(feedId: string): Promise<PipelineContentWithSteps[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${feedId}/cell-pipelines`);
    const results: CellPipelineWithSteps[] = await this.common.getJSON(url);
    return results.map(({ sourceFields, targetFields, steps, runIndex }) => ({
      sourceFields,
      targetFields,
      steps,
      runIndex,
    }));
  }
  async updatePipelines(
    feedId: string,
    pipelines: PipelineContentWithSteps[]
  ): Promise<PipelineContentWithSteps[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${feedId}/cell-pipelines`);
    const results: CellPipelineWithSteps[] = await this.common.putJSON(url, pipelines);
    return results.map(({ sourceFields, targetFields, steps, runIndex }) => ({
      sourceFields,
      targetFields,
      steps,
      runIndex,
    }));
  }

  async uploadVerify(args: {
    feedId: string;
    rows: Record<string, string>[];
    practiceId?: string;
  }): Promise<Record<string, ImportValidation>[]> {
    const { feedId, ...body } = args;
    const url = this.common.constructPath(`${this.baseEndpoint}/${feedId}/uploads/import/verify`);
    return await this.common.postJSON(url, body);
  }

  async uploadParse(args: {
    feedId: string;
    csv: string;
    practiceId?: string;
  }): Promise<Record<string, string>[]> {
    const { feedId, ...body } = args;
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${args.feedId}/uploads/import/parse`
    );
    return await this.common.postJSON(url, body);
  }

  async uploadImport(args: {
    feedId: string;
    rows: Record<string, string>[];
    append?: boolean;
    remove?: boolean;
    practiceId?: string;
  }): Promise<{ feedUpload: Upload }> {
    const { feedId, ...body } = args;
    const url = this.common.constructPath(`${this.baseEndpoint}/${args.feedId}/uploads/import`);
    return await this.common.postJSON(url, body);
  }

  async findFunctions(): Promise<FeedFunctionList> {
    const url = this.common.constructPath(`${this.baseEndpoint}/functions`);
    return await this.common.getJSON(url);
  }
}
